/**
 * Host-neutral wire protocol types for client-side tool execution.
 *
 * These types define the communication protocol between the server
 * (workflow) and any client host (Excel, Word, PowerPoint, mobile, etc.).
 * They are intentionally decoupled from any specific host implementation.
 *
 * @see ADR-043 — Host-Neutral Client-Side Tool Protocol
 */

import { z } from "zod";

/**
 * Sent from server → client via SSE data stream part.
 * Tells the client to execute a tool in the host's local API context.
 */
export interface ClientToolRequest {
  args: Record<string, unknown>;
  toolCallId: string;
  toolName: string;
  type: "client-tool-request";
  webhookUrl: string;
}

/**
 * Sent from client → server via POST to the webhook URL.
 * Contains the result of client-side tool execution.
 */
export interface ClientToolResponse {
  error?: string;
  executionTimeMs: number;
  result?: unknown;
  status: "success" | "error";
  toolCallId: string;
}

/**
 * Largest serialized {@link ClientToolResponse} a client posts to
 * `/api/chat/tool-results`, in UTF-8 bytes. Below the 4.5 MB request-body cap
 * of serverless hosts (Vercel Functions, ADR-076) with room for the envelope.
 * A POST over the cap is refused before the route runs, which leaves the run
 * waiting out its client-tool deadline instead of getting an error the model
 * can act on, so a client sends an error result in its place.
 */
export const MAX_CLIENT_TOOL_RESULT_BYTES = 4_000_000;

/**
 * Optional progress update (separate from the webhook flow).
 * Sent from client → server for progress updates on long-running tools.
 */
export interface ClientToolProgress {
  message?: string;
  partialResult?: unknown;
  status: "executing" | "partial" | "complete" | "error";
  toolCallId: string;
}

// ---------------------------------------------------------------------------
// Result sinks
// ---------------------------------------------------------------------------

export const excelRangeSpillModeValues = ["always", "oversize"] as const;
export const excelRangeSpillFormatValues = ["json", "csv"] as const;
export type ExcelRangeSpillFormat =
  (typeof excelRangeSpillFormatValues)[number];

const excelRangeSinkSchema = z.object({
  kind: z.literal("excel-range"),
  /** `always` saves every read; `oversize` only one too large to return inline. */
  mode: z.enum(excelRangeSpillModeValues),
});

/**
 * Server-side handling a client tool declares for its successful result
 * (`clientTool({ resultSink })`). `/api/chat/tool-results` applies it before
 * resuming the run, so the run journal and the model get what the sink
 * returns rather than the raw client payload.
 *
 * - `excel-range`: save an Excel range read to the chat's outputs and return
 *   an {@link ExcelRangeSpillSummary} in its place.
 */
export const clientToolResultSinkSchema = z.discriminatedUnion("kind", [
  excelRangeSinkSchema,
]);
export type ClientToolResultSink = z.infer<typeof clientToolResultSinkSchema>;

const excelRangeSpillTransformSchema = excelRangeSinkSchema.extend({
  chatId: z.string().min(1),
  /** Model-chosen filename, relative to the outputs directory. */
  file: z.string().min(1).optional(),
  format: z.enum(excelRangeSpillFormatValues).optional(),
});
export type ExcelRangeSpillTransform = z.infer<
  typeof excelRangeSpillTransformSchema
>;

/**
 * A {@link ClientToolResultSink} bound to one dispatched call. Stored with the
 * pending hook and parsed back by the result route, which is why it is a
 * schema rather than a bare type: Redis hands back unvalidated JSON.
 */
export const clientToolResultTransformSchema = z.discriminatedUnion("kind", [
  excelRangeSpillTransformSchema,
]);
export type ClientToolResultTransform = z.infer<
  typeof clientToolResultTransformSchema
>;

/** What the model (and the tool view) receives for a range saved to a file. */
export interface ExcelRangeSpillSummary {
  address?: string;
  columnCount: number;
  /** Absolute sandbox path of the values file. */
  file: string;
  format: ExcelRangeSpillFormat;
  formulasFile?: string;
  note: string;
  /** The first rows and columns of the range, long cells shortened. */
  preview: unknown[][];
  rowCount: number;
  savedToFile: true;
  textFile?: string;
}

export function isExcelRangeSpillSummary(
  value: unknown
): value is ExcelRangeSpillSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    v.savedToFile === true &&
    typeof v.file === "string" &&
    typeof v.rowCount === "number" &&
    typeof v.columnCount === "number" &&
    Array.isArray(v.preview)
  );
}
